<template>
  <fieldset class="pt-3">
    <v-row
      v-if="$slots.AddressDropDown">
      <v-col
        cols="12"
        md="6">
        <slot name="AddressDropDown"></slot>
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
        md="6"
        class="pb-4">
        <FormTextInput
          ref="streetAddress"
          v-model="userFormInput[fieldNames.streetAddress]"
          :persist-label="persistLabel"
          :vuelidate="vuelidate[fieldNames.streetAddress]"
          :input-label="formLabels[fieldNames.streetAddress]"
          input-type="text"
          :disabled="disabled"
          @input="$emit('address-input')"></FormTextInput>
      </v-col>
      <v-col
        cols="12"
        md="6"
        class="pb-4">
        <FormTextInput
          ref="addressUnitNumber"
          v-model="userFormInput[fieldNames.addressUnitNumber]"
          :persist-label="persistLabel"
          :vuelidate="vuelidate[fieldNames.addressUnitNumber]"
          :input-label="formLabels[fieldNames.addressUnitNumber]"
          input-type="text"
          :disabled="disabled"
          @input="$emit('address-input')"></FormTextInput>
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
        md="4"
        class="pb-4">
        <FormTextInput
          ref="postalCode"
          v-model="userFormInput[fieldNames.postalCode]"
          :persist-label="persistLabel"
          :vuelidate="vuelidate[fieldNames.postalCode]"
          :input-label="formLabels[fieldNames.postalCode]"
          input-type="text"
          :disabled="disabled"
          @input="onPostalCodeChange($event)"></FormTextInput>
      </v-col>
      <v-col
        cols="12"
        md="4"
        class="pb-4">
        <FormTextInput
          ref="city"
          v-model="userFormInput[fieldNames.city]"
          :persist-label="persistLabel"
          :vuelidate="vuelidate[fieldNames.city]"
          :input-label="formLabels[fieldNames.city]"
          input-type="text"
          :disabled="disabled"
          @input="$emit('address-input')"></FormTextInput>
      </v-col>
      <v-col
        cols="12"
        md="4"
        class="pb-4">
        <FormDropdown
          v-if="states.length >= 2"
          v-model="userFormInput[fieldNames.state]"
          :persist-label="persistLabel"
          :vuelidate="vuelidate[fieldNames.state]"
          :input-label="formLabels[fieldNames.state]"
          :show-value="true"
          :options="states"
          :disabled="disabled"
          @input="$emit('address-input')"></FormDropdown>
        <FormTextInput
          v-else
          v-model="userFormInput[fieldNames.state]"
          :persist-label="persistLabel"
          :vuelidate="vuelidate[fieldNames.state]"
          :input-label="formLabels[fieldNames.state]"
          input-type="text"></FormTextInput>
      </v-col>
    </v-row>
  </fieldset>
</template>

<script>
import axios from 'ca-http-service'
import FormTextInput from '@/components/form/FormTextInput.vue'
import FormDropdown from '@/components/form/FormDropdown.vue'

export default {
  name: 'FormGroupAddressInfo',
  components: {
    FormTextInput,
    FormDropdown
  },
  props: {
    vuelidate: {
      type: Object,
      required: true
    },
    // NOTE: make sure to pass the formData reference, instead of the copy of the userFormInput object to this prop
    formData: {
      type: Object,
      required: true
    },
    formLabels: {
      type: Object,
      required: true
    },
    persistLabel: {
      type: Boolean,
      required: false,
      default: false
    },
    updateByPostalCode: {
      type: Boolean,
      required: false,
      default: true
    },
    fieldNames: {
      type: Object,
      required: false,
      // TODO: this should support streetAddress1 and streetAddress2
      default: () => ({
        streetAddress: 'streetAddress',
        addressUnitNumber: 'addressUnitNumber',
        city: 'city',
        state: 'state',
        postalCode: 'postalCode'
      })
    },
    country: {
      type: String,
      required: false,
      default: 'US'
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    updateInputMethod: {
      type: [Boolean, Function],
      required: false,
      default: () => false
    }
  },
  data () {
    return {
      userFormInput: this.getFormClass(),
      states: []
    }
  },
  watch: {
    country: {
      async handler (newVal, oldVal) {
        this.states = await this.getStates(newVal)
      }
    }
  },
  async mounted () {
    this.userFormInput = this.formData
    this.states = await this.getStates()
  },
  methods: {
    getFormClass () {
      return {
        [this.fieldNames.streetAddress]: '',
        [this.fieldNames.addressUnitNumber]: '',
        [this.fieldNames.city]: '',
        [this.fieldNames.state]: '',
        [this.fieldNames.postalCode]: ''
      }
    },
    updateFields (payload) {
      const keys = Object.keys(payload)
      keys.map(k => {
        const value = payload[k]
        if (typeof this.$refs[k] === 'undefined') return k

        if (!this.updateInputMethod) {
          this.defaultUpdateInput(this.$refs[k].$el, value)
          return k
        }
        this.updateInputMethod(this.$refs[k].$el, value)
        return k
      })
    },
    async getStates (country = 'US') {
      return axios.get('/generic/getStateList', {
        params: {
          countryCode: country
        }
      })
        .then((res) => {
          if (res.status === 200) {
            return res.data
          } else {
            return []
          }
        })
        .catch((err) => {
          console.error(err)
        })
    },
    async onPostalCodeChange (event) {
      if (this.updateByPostalCode) {
        if (event.length === 5) {
          const results = await this.getCityStateByPostalCode(event)
          if (results != null && results.city != null) {
            // set values
            this.userFormInput[this.fieldNames.city] = results.city
            this.userFormInput[this.fieldNames.state] = results.state
          } else {
            // clear values
            this.userFormInput[this.fieldNames.city] = ''
            this.userFormInput[this.fieldNames.state] = ''
          }
          // validate newly set values
          if (this.vuelidate[this.fieldNames.city]) {
            this.vuelidate[this.fieldNames.city].$touch()
          }
          if (this.vuelidate[this.fieldNames.state]) {
            this.vuelidate[this.fieldNames.state].$touch()
          }
        }
      }
      this.$emit('address-input')
    },
    async getCityStateByPostalCode (postalCode) {
      return axios.get('/api/AddressVerification/GetCityAndState', { params: { zip: postalCode } })
        .then((res) => {
          if (res.status === 200) {
            return res.data
          } else {
            return null
          }
        })
        .catch((err) => {
          const errorMessage = err.response.data
          console.error(errorMessage)
        })
    },
    defaultUpdateInput (inputParentElm, value) {
      const inputs = inputParentElm.getElementsByTagName('INPUT')
      const inputsArray = Array.prototype.slice.call(inputs)
      inputsArray.map((i) => {
        i.value = value
        return i
      })
    }
  }
}
</script>
